/**
 * The clearance beat-state enum, in ONE place.
 *
 * This is a DATA CONTRACT, not presentation, which is why it does not live in
 * beatPresentation.js beside the colours it drives. It mirrors the enum
 * `ClearanceStateResolver` actually emits (backend
 * app/Services/Credential/ClearanceStateResolver.php:37, mapped at :143-152):
 *
 *   locked | active | cleared | waived | failed_terminal
 *
 * The stored beat_states table has a WIDER vocabulary - `available` and
 * `in_progress` are both real rows - and the resolver COLLAPSES both to
 * `active` before the payload leaves the server. That collapse is the reason
 * this file exists. The first plan for the terminal exam gated its click and
 * its band on `available`/`in_progress`, states the API never emits, so every
 * gate would have been permanently false and the frozen-after-ship symptom
 * would have survived a green test suite built on invented fixtures (codex
 * round-1 finding 1).
 *
 * So: every frontend state comparison goes through these constants, and the
 * fixtures assert their own state strings against BEAT_STATE_VALUES. A test
 * that invents a state name now fails at the fixture, not in production.
 */

export const BEAT_STATES = Object.freeze({
  LOCKED: "locked",
  ACTIVE: "active",
  CLEARED: "cleared",
  WAIVED: "waived",
  FAILED_TERMINAL: "failed_terminal",
});

/** Every state the clearance API can emit. Order is the resolver's own. */
export const BEAT_STATE_VALUES = Object.freeze([
  BEAT_STATES.LOCKED,
  BEAT_STATES.ACTIVE,
  BEAT_STATES.CLEARED,
  BEAT_STATES.WAIVED,
  BEAT_STATES.FAILED_TERMINAL,
]);

/**
 * isKnownBeatState - is this a state the clearance API can actually emit?
 * Used by the fixtures to refuse an invented state name at construction time.
 *
 * @param {*} state
 * @returns {boolean}
 */
export function isKnownBeatState(state) {
  return BEAT_STATE_VALUES.includes(state);
}

/**
 * A beat the learner has finished, by either route. `waived` is an instructor
 * clearing it for you and reads to the learner exactly like `cleared`
 * (beatPresentation.stateVisual has said so since F1b).
 *
 * @param {string|null|undefined} state
 * @returns {boolean}
 */
export function isSettledCleared(state) {
  return state === BEAT_STATES.CLEARED || state === BEAT_STATES.WAIVED;
}
